import React, { useState } from 'react';
import { FormProvider, RHFDateField, RHFDateTimeField, RHFTextField } from '../../../common/hook-form';
import { CourierParentFilter, CourierServiceTypeFilter, resolveCourierIdsForParentAndServiceType } from '../../../common/hook-form/CourierFilter';
import useMerchantAllocatedCouriers from '../../../hooks/useMerchantAllocatedCouriers';
import { useForm } from 'react-hook-form';
import { Box, Grid, Divider, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { apiConstants, postGeneric } from '../../../common/constants';
import { toast } from "react-toastify";
import { useSelector } from 'react-redux';
import { convertDate, convertDateTimeISO } from '../../../helpers/UtilityHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import { FONT, WEIGHT } from '../../../styles/foundation/typography';
import { SUBTLE, BORDER } from '../../../styles/foundation/colors';
import { FormField, ToggleField, modernInputsSx, ctaBtnSx, ghostBtnSx } from './_downloadUi';

const LabelDownloadBulk = () => {
    const merchantId = useSelector((state) => state.user.userInfo.merchant.id);
    const { couriers } = useMerchantAllocatedCouriers(merchantId);
    const [isDownloadingFile, setIsDownloadingFile] = useState(false)

    const method = useForm({
        resolver: yupResolver(Yup.object().shape({
            date: Yup.date().typeError('Date is required').required('Date is required'),
            fromTime: Yup.date().nullable().typeError('Invalid time')
                .when('toTime', { is: (to) => !!to, then: (schema) => schema.required('From time is required') }),
            toTime: Yup.date().nullable().typeError('Invalid time')
                .when('fromTime', {
                    is: (from) => !!from,
                    then: (schema) => schema.required('To time is required')
                        .test('after-from', 'To time must be after From time', function (toTime) {
                            const fromTime = this.parent.fromTime;
                            if (!fromTime || !toTime) return true;
                            return new Date(toTime).getTime() > new Date(fromTime).getTime();
                        }),
                }),
            pincode: Yup.string()
                .test('valid-pincode', 'Invalid pincode', value => { if (!value) return false; const regex = /^\d{6}$/; return regex.test(value) || value.length !== 6; })
                .required("Pincode is a mandatory field")
                .test('length', 'Please enter a 6 digit pincode', value => !value || value.length === 6)
                .test('valid', 'Please enter a valid pincode', value => { if (!value || value.length !== 6) return true; return /^\d{6}$/.test(value); }),
        }, [['fromTime', 'toTime']])),
        defaultValues: { pincode: '', date: null, fromTime: null, toTime: null, courierParent: '', serviceType: [], sku: '', cancelled_orders: false, manifested_orders: false }
    });
    const { handleSubmit, reset, watch } = method;
    const fromTimeWatch = watch('fromTime');
    const courierParentWatch = watch('courierParent');

    const buildSlotDateTime = (date, time) => {
        const d = new Date(date); const t = new Date(time);
        d.setHours(t.getHours(), t.getMinutes(), 0, 0);
        return convertDateTimeISO(d);
    };

    const onSubmit = async (data) => {
        // Mirrors the admin panel's Courier/Service Type filter bar: Service Type is only
        // required once a Courier is picked. Leaving Courier blank downloads exactly as before.
        if (data.courierParent && (!Array.isArray(data.serviceType) || data.serviceType.length === 0)) {
            toast("Please select at least one Service Type for the chosen Courier.", { type: 'error', theme: "colored" })
            return;
        }
        setIsDownloadingFile(true)
        try {
            const params = new URLSearchParams();
            if (data.fromTime && data.toTime) {
                params.append('fromDateTime', buildSlotDateTime(data.date, data.fromTime));
                params.append('toDateTime', buildSlotDateTime(data.date, data.toTime));
            } else { params.append('date', convertDate(data.date)); }
            params.append('pincode', data.pincode);
            params.append('merchantId', merchantId);
            params.append('shipmentType', 'B2C');
            params.append('includeCancelOrders', data.cancelled_orders);
            params.append('includeManifested', data.manifested_orders);
            const courierIds = resolveCourierIdsForParentAndServiceType(couriers, data.courierParent, data.serviceType);
            if (courierIds) params.append('courierIds', courierIds);
            if (data.sku) params.append('sku', data.sku);

            await postGeneric(`${apiConstants.GENERATE_BULK_LABELS}?${params.toString()}`).then(response => {
                window.open(response.data?.mergedPdf, true)
            }).catch(err => {
                console.error(err);
                const message = err?.response?.data?.message || err.message
                toast(message || "Error downloading file", { type: 'error', theme: "colored" })
                setIsDownloadingFile(false);
            });
        } catch (err) {
            const message = err?.response?.data?.message || err.message
            toast(message || "Error downloading file", { type: 'error', theme: "colored" })
        }
        setIsDownloadingFile(false)
    }

    return (
        <FormProvider methods={method} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={modernInputsSx}>
                <Grid container spacing={2} rowSpacing={2.25}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormField label="Pincode" required>
                            <RHFTextField name="pincode" label="" placeholder="e.g. 560001" />
                        </FormField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormField label="Date">
                            <RHFDateField name="date" label="" />
                        </FormField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormField label="Courier">
                            <CourierParentFilter couriers={couriers} merchantId={merchantId} hideNoneHint label="" />
                        </FormField>
                    </Grid>
                    {courierParentWatch && (
                        <Grid item xs={12} sm={6} md={4}>
                            <FormField label="Service Type" required>
                                <CourierServiceTypeFilter couriers={couriers} parent={courierParentWatch} label="" />
                            </FormField>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                        <FormField label="From Time (optional)">
                            <RHFDateTimeField name="fromTime" mode="time" label="" />
                        </FormField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormField label="To Time (optional)">
                            <RHFDateTimeField name="toTime" mode="time" isEndTime startTime={fromTimeWatch} label="" />
                        </FormField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormField label="SKU (optional)">
                            <RHFTextField name="sku" label="" placeholder="e.g. SKU123" />
                        </FormField>
                    </Grid>
                </Grid>

                {/* Options band — modern toggles */}
                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <TuneOutlinedIcon sx={{ fontSize: 17, color: SUBTLE }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.semibold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Include in download
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <ToggleField name="cancelled_orders" label="Cancelled Orders" icon={<CancelOutlinedIcon />} />
                        <ToggleField name="manifested_orders" label="Manifested Orders" icon={<Inventory2OutlinedIcon />} />
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: BORDER }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small" sx={ghostBtnSx}
                    startIcon={<RestartAltIcon />} onClick={() => reset()}>
                    Reset
                </Button>
                <LoadingButton type="submit" loading={isDownloadingFile} variant="contained" size="small"
                    sx={ctaBtnSx} startIcon={<DownloadIcon />}>
                    Download Labels
                </LoadingButton>
            </Box>
        </FormProvider>
    )
}
export default LabelDownloadBulk;
